import type { CSSProperties } from '@mantine/core'
import type { GroupOrderCardProps } from './GroupOrderCard.types'
import type { OrderDetails } from '@/entities/order/model/order.types'
import { Box, Button, Flex, Group, Stack, Text } from '@mantine/core'
import { clsx } from 'clsx'
import { extractRgbValues } from '@/shared/lib/mantine/mantine.utils'
import { useIsLgUp } from '@/shared/lib/style/breakpoints'
import { OrderItemWithMetaInfo } from '../order-item-with-meta-info'
import classes from './GroupOrderCard.module.css'

export function GroupOrderCard({
  cardTitle,
  cardSubtitle,
  orders,
  loading,
  actionText,
  animate,
  outline,
  color,
  statusText,
  statusColor,
  className,
  onClick,
  onOrderItemClick,
  onAction,
  renderStatus,
  dataOnboarding,
}: GroupOrderCardProps) {
  const isLgUp = useIsLgUp()

  const pulseStyle = (): CSSProperties | undefined => {
    const effectiveColor = color || statusColor

    if (!effectiveColor || (!animate && !outline))
      return

    const _pulseColorRGBA = `rgba(${extractRgbValues(effectiveColor)}, 0.7)`
    const _pulseColorRGBValues = extractRgbValues(effectiveColor, false)

    return {
      '--status-color': _pulseColorRGBA,
      '--pulse-color': _pulseColorRGBValues,
    } as CSSProperties
  }

  function handleCardClick() {
    if (loading) {
      return
    }

    if (onClick !== undefined) {
      onClick()
    }
  }

  function handleOrderItemClick(e: React.MouseEvent, orderId: OrderDetails['orderId']) {
    e.stopPropagation()

    if (loading) {
      return
    }

    if (onOrderItemClick !== undefined) {
      onOrderItemClick(orderId)
    }
  }

  function handleActionClick(e: React.MouseEvent) {
    e.stopPropagation()

    if (loading) {
      return
    }

    if (onAction !== undefined) {
      onAction()
    }
  }

  return (
    <div
      className={clsx({
        [classes.groupOrderCard]: true,
        [classes.cardOutline]: (animate || outline) && (color || statusColor),
        [classes.cardAnimationPulse]: animate && (color || statusColor),
        [classes.cursorDisabled]: loading,
        [className as string]: !!className,
      })}
      style={pulseStyle()}
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      data-testid="group-order-card"
      data-onboarding={dataOnboarding}
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start" gap="4px">
        <Flex direction="column" miw={0}>
          <Text
            span
            lineClamp={1}
            className={classes.cardTitle}
          >
            {cardTitle}
          </Text>
          <Text
            span
            lineClamp={1}
            className={classes.cardSubtitle}
          >
            {cardSubtitle}
          </Text>
        </Flex>

        {renderStatus
          ? renderStatus()
          : statusText && statusColor
            ? (
                <Box
                  bg={statusColor}
                  className={classes.statusText}
                >
                  {statusText}
                </Box>
              )
            : null}
      </Group>

      <Stack gap="xs" align="flex-end">
        {orders.map((order) => {
          return (
            <OrderItemWithMetaInfo
              className={clsx(classes.orderItem, {
                [classes.cursorDisabled]: loading,
              })}
              bg="gray.1"
              key={order.orderId}
              customerName={order.customerName}
              orderId={order.orderId}
              onClick={handleOrderItemClick}
            />
          )
        })}

        {actionText && onAction && (
          <Button
            loading={loading}
            color={color || statusColor || 'violet'}
            variant="outline"
            size={isLgUp ? 'md' : 'sm'}
            className={classes.actionButton}
            onClick={handleActionClick}
          >
            {actionText}
          </Button>
        )}
      </Stack>
    </div>
  )
}
